import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import EquipmentService from "../app/data/equipment/equipmentServices";
import TerminalService from "../app/data/terminal-summary/terminalsummaryService";
import { QUERY_KEYS } from "app/data/common/queryKeys"; 

const equipmentService = EquipmentService.getInstance();
const terminalService = TerminalService.getInstance();

interface DocumentToUpload {
  fileType: 'REGISTRATION' | 'DOT_INSPECTION';
  file: File;
}

export const useTerminalsList = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_TERMINALS_LIST],
    queryFn: () => terminalService.getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true),

    select: (res) => {
      return (res?.data?.content || []).map((terminal: any) => ({
        label: terminal.terminalName,
        value: terminal.terminalName,
      }));
    },
    staleTime: 1000 * 60 * 10,
  });
};

export const useEquipmentTypes = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_EQUIPMENT_TYPES],
    queryFn: () => equipmentService.getEquipmentTypes(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useProductLines = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_PRODUCT_LINES],
    queryFn: () => equipmentService.getProductLines(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useAssetDescriptor = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_ASSET_DESCRIPTOR],
    queryFn: () => equipmentService.getAssetDescriptor(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useLeaseTypes = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_LEASE_TYPES],
    queryFn: () => equipmentService.getLeaseTypes(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useMakes = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_MAKES],
    queryFn: () => equipmentService.getMakes(),
    select: (res) => {
      return (res || []).map((item: string) => ({
        label: item,
        value: item,
      }));
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useEquipmentList = (
  search?: string,
  type?: string,
  status?: string,
  terminal?: string
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEYS.GET_EQUIPMENT_LIST, search, type, status, terminal],
    queryFn: ({ pageParam = 0 }) =>
      equipmentService.getEquipmentList(search, pageParam, type, status, terminal),

    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.number ?? 0;
      const totalPages = lastPage?.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },

    select: (data) => {
      const allItems = data.pages.flatMap((p) => p.content);
      return { ...data, allItems };
    },

    staleTime: 1000 * 60 * 5,
  });
};


export const useEquipmentListDetails = (equipmentNumber: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_EQUIPMENT_DETAIL, equipmentNumber],
    queryFn: () => equipmentService.getEquipmentDetails(equipmentNumber),
    enabled: !!equipmentNumber,
    staleTime: 1000 * 60 * 5,
  });
};

// CREATE
export const useCreateEquipmentWithDocuments = () => {
  const queryClient = useQueryClient();
  const uploadFileMutation = useUploadEquipmentFile();

  return useMutation({
    mutationFn: async ({ payload, filesToUpload }: { payload: any, filesToUpload: DocumentToUpload[] }) => {
      const createResponse = await EquipmentService.getInstance().createEquipment(payload);

      const equipmentId = createResponse?.data?.id;

      if (!equipmentId) {
        throw new Error("Failed to upload documents: Equipment ID not returned after creation.");
      }

      const uploadPromises = filesToUpload.map(async (doc) => {
        return await uploadFileMutation.mutateAsync({
          equipmentId,
          fileType: doc.fileType,
          file: doc.file,
        });
      });

      await Promise.all(uploadPromises);

      return createResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_LIST] });
    },
    onError: (error: any) => {
      throw new Error(error.message || "Failed to create equipment or upload documents.");
    }
  });
};

// UPDATE
export const useUpdateEquipment = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, payload }: any) => EquipmentService.getInstance().updateEquipment(id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_LIST] });
      },
      onError: (error: any) => {
        throw new Error(error.message);
      }
    }
  );
};

// DELETE
export const useDeleteEquipment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => equipmentService.deleteEquipment(id),

    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.GET_EQUIPMENT_LIST]);
    },
  });
};

export const useUploadEquipmentFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ equipmentId, fileType, file }: UploadFilePayload) => {
      if (equipmentId) {
        return equipmentService.uploadEquipmentFile(equipmentId, fileType, file);
      } else {
        return equipmentService.uploadInitialDocument(file);
      }
    },

    onSuccess: (data, variables) => {
      if (variables.equipmentId) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_DETAIL, variables.equipmentId] });
      }
    },
  });
};

export const useDeleteEquipmentFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ equipmentId, fileType }: { equipmentId: string, fileType: string }) =>
      equipmentService.deleteEquipmentFile(equipmentId, fileType),

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EQUIPMENT_DETAIL, variables.equipmentId] });
    },
  });
};
